import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  Landmark,
  Mail,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { AccessDeniedError, isValidEmail, signIn, signUp } from '@/lib/auth';

type Mode = 'login' | 'create';

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isValidEmail(email)) {
      setError('Enter a valid work email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (mode === 'create' && name.trim().length < 2) {
      setError('Tell us your name so outreach letters can be signed.');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'create') {
        signUp(name, email, password);
      } else {
        signIn(email, password);
      }
      navigate('/');
    } catch (err) {
      if (err instanceof AccessDeniedError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
      setSubmitting(false);
    }
  };

  const isCreate = mode === 'create';

  return (
    <div className="grid min-h-screen grid-cols-1 bg-canvas p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-6 lg:p-6">
      {/* Form side */}
      <section className="flex flex-col rounded-2xl bg-surface p-8 shadow-card lg:p-14">
        {/* Brand */}
        <Link to="/auth" className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-card">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
              <path
                d="M4 7h16M4 12h10M4 17h7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="text-[1.0625rem] font-bold tracking-tight text-ink">
              RLPScout
            </div>
            <div className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-ink-muted">
              Federal Lease Intelligence
            </div>
          </div>
        </Link>

        <div className="mx-auto my-auto flex w-full max-w-md flex-col gap-8 py-10">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
              <span className="kicker text-brand-700">
                {isCreate ? 'Start your workspace' : 'Welcome back, operator'}
              </span>
            </div>
            <h1 className="text-[2rem] font-bold leading-tight tracking-head text-ink">
              {isCreate ? 'Create your account' : 'Sign in to RLPScout'}
            </h1>
            <p className="text-[0.9375rem] leading-relaxed text-ink-muted">
              {isCreate
                ? 'Set up the workspace that watches every federal lease RLP for you — and drafts the outreach before your competition even reads the notice.'
                : 'Pick up where you left off. Your solicitations, candidates, and pending drafts are waiting.'}
            </p>
          </div>

          {/* Mode toggle */}
          <div
            role="tablist"
            aria-label="Authentication mode"
            className="flex items-center gap-1 rounded-xl border border-rule bg-surface-sunk p-1"
          >
            <ModeTab
              active={mode === 'login'}
              onClick={() => {
                setMode('login');
                setError(null);
              }}
            >
              Sign in
            </ModeTab>
            <ModeTab
              active={mode === 'create'}
              onClick={() => {
                setMode('create');
                setError(null);
              }}
            >
              Create account
            </ModeTab>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="auth-form">
            {isCreate && (
              <FormField label="Full name" htmlFor="auth-name">
                <Input
                  id="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  autoComplete="name"
                  required
                  className="h-11"
                  data-testid="auth-name"
                />
              </FormField>
            )}

            <FormField label="Work email" htmlFor="auth-email">
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" />
                <Input
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  className="h-11 pl-9"
                  data-testid="auth-email"
                />
              </div>
            </FormField>

            <FormField
              label="Password"
              htmlFor="auth-password"
              aside={
                !isCreate ? (
                  <button
                    type="button"
                    className="text-[0.75rem] font-semibold text-brand-700 hover:text-brand-800"
                    onClick={() =>
                      setError(
                        'Reach out at hello@rlpscout.ai to reset — we’ll wire this up shortly.'
                      )
                    }
                  >
                    Forgot password?
                  </button>
                ) : null
              }
            >
              <div className="relative">
                <Input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isCreate ? 'Create a strong password' : 'Enter your password'}
                  autoComplete={isCreate ? 'new-password' : 'current-password'}
                  required
                  minLength={8}
                  className="h-11 pr-10"
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-ink-dim hover:text-ink"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {isCreate && (
                <p className="mt-1 text-[0.6875rem] text-ink-muted">
                  8+ characters. Use a passphrase, not your pet's name.
                </p>
              )}
            </FormField>

            {error && (
              <div
                className="rounded-lg border border-danger-border bg-danger-soft px-3 py-2 text-[0.8125rem] text-danger-bold"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="mt-2 h-12 w-full"
              data-testid="auth-submit"
            >
              {submitting
                ? 'Signing you in…'
                : isCreate
                  ? 'Create account'
                  : 'Sign in'}
              <ArrowRight className="h-4 w-4" />
            </Button>

            <p className="text-center text-[0.8125rem] text-ink-muted">
              {isCreate ? 'Already have an account? ' : 'New to RLPScout? '}
              <button
                type="button"
                className="font-semibold text-brand-700 hover:text-brand-800"
                onClick={() => {
                  setMode(isCreate ? 'login' : 'create');
                  setError(null);
                }}
              >
                {isCreate ? 'Sign in' : 'Create an account'}
              </button>
            </p>
          </form>
        </div>

        <footer className="mt-auto flex flex-wrap items-center justify-between gap-2 text-[0.6875rem] text-ink-muted">
          <span>© {new Date().getFullYear()} Occasion Crafters LLC — RLPScout</span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Encrypted in transit
          </span>
        </footer>
      </section>

      {/* Hero side */}
      <aside className="relative hidden overflow-hidden rounded-2xl bg-sidebar text-white shadow-card lg:block">
        {/* Atmospheric gradients */}
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle at 85% 15%, rgba(45,212,191,0.35), transparent 45%), radial-gradient(circle at 15% 85%, rgba(20,184,166,0.25), transparent 50%), radial-gradient(circle at 50% 50%, rgba(15,118,110,0.18), transparent 60%)',
          }}
        />
        {/* Geometric mesh */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.08]"
          viewBox="0 0 600 800"
          fill="none"
          aria-hidden
        >
          <defs>
            <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="600" height="800" fill="url(#grid)" />
          <path
            d="M -40 650 L 240 430 L 120 220 L 540 40"
            stroke="rgba(94,234,212,0.6)"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M 600 700 L 320 520 L 460 320 L 200 120"
            stroke="rgba(94,234,212,0.35)"
            strokeWidth="1.2"
            fill="none"
          />
        </svg>

        <div className="relative flex h-full flex-col justify-between p-10 xl:p-14">
          {/* Top kicker */}
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
            <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-brand-200">
              Federal lease intelligence
            </span>
          </div>

          {/* Headline */}
          <div className="flex max-w-lg flex-col gap-5">
            <h2 className="text-[2.5rem] font-bold leading-[1.05] tracking-head xl:text-[3rem]">
              Every GSA RLP.
              <br />
              <span className="bg-gradient-to-r from-brand-300 via-brand-200 to-white bg-clip-text text-transparent">
                Read, ranked, and actioned.
              </span>
            </h2>
            <p className="max-w-md text-[0.9375rem] leading-relaxed text-white/75">
              RLPScout watches SAM.gov around the clock, extracts the requirements the
              moment a notice drops, sources candidate properties inside the delineated
              area, and drafts broker outreach for you to approve.
            </p>
          </div>

          {/* Preview chips */}
          <div className="relative mt-8 flex flex-col gap-4">
            <PreviewCard
              icon={Landmark}
              title="GSA-08-RLP-25-017"
              subtitle="Dept. of Veterans Affairs · Denver, CO"
              valueLabel="ABOA"
              valueText="42,500 – 48,000 sf"
              pill={{ label: '12 days left', tone: 'warn' }}
              offsetClass=""
            />
            <PreviewCard
              icon={Building2}
              title="3 broker drafts ready for review"
              subtitle="Outreach writer · drafted today"
              valueLabel="Awaiting"
              valueText="Your approval"
              pill={{ label: 'Human-in-loop', tone: 'info' }}
              offsetClass="xl:translate-x-10"
            />
            <PreviewCard
              icon={Sparkles}
              title="18 candidate properties scored"
              subtitle="Property scout · within delineated area"
              valueLabel="Top match"
              valueText="91 / 100"
              pill={{ label: 'In polygon', tone: 'success' }}
              offsetClass="xl:translate-x-20"
            />
          </div>
        </div>
      </aside>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex-1 rounded-lg px-3 py-2 text-[0.8125rem] font-semibold transition-all',
        active
          ? 'bg-surface text-ink shadow-card'
          : 'text-ink-muted hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}

function FormField({
  label,
  htmlFor,
  children,
  aside,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={htmlFor} className="kicker">
          {label}
        </label>
        {aside}
      </div>
      {children}
    </div>
  );
}

type PreviewTone = 'warn' | 'info' | 'success';

function PreviewCard({
  icon: Icon,
  title,
  subtitle,
  valueLabel,
  valueText,
  pill,
  offsetClass,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  valueLabel: string;
  valueText: string;
  pill: { label: string; tone: PreviewTone };
  offsetClass: string;
}) {
  const toneClass =
    pill.tone === 'warn'
      ? 'bg-amber-400/20 text-amber-200 ring-1 ring-inset ring-amber-300/30'
      : pill.tone === 'info'
        ? 'bg-sky-400/20 text-sky-200 ring-1 ring-inset ring-sky-300/30'
        : 'bg-emerald-400/20 text-emerald-200 ring-1 ring-inset ring-emerald-300/30';
  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-md transition-transform',
        offsetClass
      )}
    >
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-card">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-mono text-[0.8125rem] font-semibold text-white">
            {title}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.08em]',
              toneClass
            )}
          >
            {pill.label}
          </span>
        </div>
        <div className="mt-0.5 truncate text-[0.75rem] text-white/60">{subtitle}</div>
      </div>
      <div className="hidden text-right sm:block">
        <div className="text-[0.5625rem] font-semibold uppercase tracking-[0.14em] text-white/50">
          {valueLabel}
        </div>
        <div className="font-mono text-[0.8125rem] font-semibold text-white">
          {valueText}
        </div>
      </div>
    </div>
  );
}
